import { api } from "./client";

export type OrganizationRow = {
  id: string;
  name: string | null;
  created_at: string | null;
};

export type OrganizationSortField = "name" | "created_at";
export type SortDir = "asc" | "desc";

export type OrganizationListResponse = {
  organizations: OrganizationRow[];
  page: number;
  page_size: number;
  total: number;
  sort: OrganizationSortField;
  dir: SortDir;
};

export type OrganizationListParams = {
  page: number;
  page_size?: number;
  q?: string;
  sort?: OrganizationSortField;
  dir?: SortDir;
};

export type OrganizationDetail = {
  id: string;
  name: string | null;
  api_key: string;
  created_at: string | null;
  updated_at: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  // Verifier API limits. Numbers, never null — the columns are NOT NULL and there
  // is no sentinel for "unlimited": 0 blocks, and effectively-unlimited is a large
  // explicit value.
  verify_rate_limit_per_hour: number;
  verify_rate_limit_per_minute: number;
};

/** Presets the tier vocabulary lives in — the schema stores plain integers, so a
 *  partner can also be given a bespoke number without inventing a tier. */
export const RATE_LIMIT_PRESETS: { label: string; perHour: number; perMinute: number }[] = [
  { label: "Trial", perHour: 60, perMinute: 10 },
  { label: "Standard", perHour: 600, perMinute: 60 },
  { label: "Partner", perHour: 6000, perMinute: 300 },
  { label: "Internal", perHour: 60000, perMinute: 3000 },
];

export const RATE_LIMIT_MAX = 1000000;

export type OrganizationPatch = Partial<{
  name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  verify_rate_limit_per_hour: number;
  verify_rate_limit_per_minute: number;
}>;

export type OrganizationCreate = {
  name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  verify_rate_limit_per_hour?: number;
  verify_rate_limit_per_minute?: number;
};

export const OrganizationsApi = {
  list(params: OrganizationListParams) {
    return api
      .get<OrganizationListResponse>("/organizations", {
        params: {
          page: params.page,
          page_size: params.page_size ?? 40,
          q: params.q || undefined,
          sort: params.sort || undefined,
          dir: params.dir || undefined,
        },
      })
      .then((r) => r.data);
  },
  options() {
    return api
      .get<{ options: { id: string; name: string | null }[] }>("/organizations/options")
      .then((r) => r.data.options);
  },
  get(id: string) {
    return api.get<OrganizationDetail>(`/organizations/${id}`).then((r) => r.data);
  },
  create(body: OrganizationCreate) {
    return api.post<OrganizationDetail>("/organizations", body).then((r) => r.data);
  },
  update(id: string, patch: OrganizationPatch) {
    return api.patch<OrganizationDetail>(`/organizations/${id}`, patch).then((r) => r.data);
  },
  regenerateApiKey(id: string) {
    return api
      .post<OrganizationDetail>(`/organizations/${id}/regenerate-api-key`)
      .then((r) => r.data);
  },
  bulkDelete(ids: string[]) {
    return api
      .delete<{ deleted: string[]; refused: string[] }>("/organizations", {
        data: { ids },
      })
      .then((r) => r.data);
  },
};
